import Fuse, { type IFuseOptions } from "fuse.js";

export type HelpAudience = "client" | "therapist";

export const HELP_AUDIENCE_SEGMENTS: Record<HelpAudience, string> = {
  client: "klijenti",
  therapist: "terapeuti",
};

export const HELP_AUDIENCE_LABELS: Record<HelpAudience, string> = {
  client: "Klijenti",
  therapist: "Terapeuti",
};

const HELP_STOP_WORDS = new Set([
  "je",
  "da",
  "li",
  "i",
  "u",
  "na",
  "za",
  "se",
  "sa",
  "od",
  "do",
  "ili",
  "ne",
  "mi",
  "me",
  "te",
  "su",
  "bi",
  "ce",
  "ka",
  "iz",
  "po",
  "a",
  "o",
  "ni",
  "to",
  "tu",
  "ta",
  "ti",
  "mu",
  "im",
  "ih",
  "ga",
  "ko",
  "sto",
  "sta",
  "sve",
  "svi",
  "taj",
  "ova",
  "ove",
  "bas",
  "kako",
  "gde",
  "mogu",
  "moze",
  "moli",
  "molim",
  "treba",
  "trebam",
  "zelim",
  "imam",
  "moj",
  "moja",
  "moje",
  "pronadjem",
  "nadjem",
  "the",
  "can",
  "how",
  "where",
  "is",
  "my",
  "to",
  "for",
  "and",
]);

export function normalizeHelpSearch(text: string) {
  return text
    .toLowerCase()
    .replace(/ć/g, "c")
    .replace(/č/g, "c")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .replace(/đ/g, "dj");
}

export function buildSearchTerms(query: string) {
  const normalized = normalizeHelpSearch(query)
    .replace(/\bappstore\b/g, "app store")
    .replace(/\bparental consent\b/g, "saglasnost roditelja")
    .replace(/\bno[ -]show\b/g, "nedolazak")
    .replace(/\btext[ -]only\b/g, "dopisivanje")
    .replace(/\bdownload\b/g, "preuzimanje");
  return [
    ...new Set(
      normalized
        .split(/[^\p{L}\p{N}]+/u)
        .filter((term) => term.length > 1 && !HELP_STOP_WORDS.has(term)),
    ),
  ];
}

function searchWithTerms<T>(
  items: T[],
  options: IFuseOptions<T>,
  query: string,
) {
  const fuse = new Fuse(items, options);
  const results = new Map<T, number>(
    fuse
      .search(normalizeHelpSearch(query))
      .map((result) => [result.item, result.score ?? 0]),
  );
  const terms = buildSearchTerms(query);
  const termMatches = new Map<T, number[]>();
  for (const term of terms) {
    for (const result of fuse.search(term)) {
      const scores = termMatches.get(result.item) ?? [];
      scores.push(result.score ?? 0);
      termMatches.set(result.item, scores);
    }
  }
  // A single generic word must not rescue an unrelated multi-word request.
  const minimumMatches = Math.min(
    terms.length,
    Math.max(2, Math.ceil(terms.length * 0.6)),
  );
  for (const [item, scores] of termMatches) {
    if (scores.length < minimumMatches) continue;
    const coverage = scores.length / terms.length;
    const score =
      0.5 +
      scores.reduce((sum, value) => sum + value, 0) / scores.length / 2 -
      coverage / 4;
    results.set(item, Math.min(results.get(item) ?? 1, score));
  }
  return [...results]
    .map(([item, score]) => ({ item, score }))
    .sort((a, b) => a.score - b.score);
}

export type HelpQuestion = {
  question: string;
  answer?: string;
};

export type SearchableArticle = {
  slug: string;
  title: string;
  description: string;
  category: string;
  audience: HelpAudience;
  timeToRead: number;
  keywords: string[];
  questions: HelpQuestion[];
  tocText: string[];
  plainText?: string;
};

export type SearchableHelpSection = {
  id: string;
  source: string;
  slug: string;
  href: string;
  title: string;
  description: string;
  category: string;
  audience: HelpAudience;
  heading: string;
  snippet: string;
  content: string;
  timeToRead: number;
  keywords: string[];
  questions: HelpQuestion[];
};

function normalizeHelpQuestions(questions: HelpQuestion[]): string[] {
  return questions.flatMap((entry) =>
    entry.answer
      ? [normalizeHelpSearch(entry.question), normalizeHelpSearch(entry.answer)]
      : [normalizeHelpSearch(entry.question)],
  );
}

const articleFuseOptions: IFuseOptions<SearchableArticle> = {
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
  threshold: 0.35,
  keys: [
    {
      name: "title",
      weight: 0.4,
      getFn: (article) => normalizeHelpSearch(article.title),
    },
    {
      name: "keywords",
      weight: 0.24,
      getFn: (article) => article.keywords.map(normalizeHelpSearch),
    },
    {
      name: "questions",
      weight: 0.18,
      getFn: (article) => normalizeHelpQuestions(article.questions),
    },
    {
      name: "description",
      weight: 0.1,
      getFn: (article) => normalizeHelpSearch(article.description),
    },
    {
      name: "tocText",
      weight: 0.08,
      getFn: (article) => article.tocText.map(normalizeHelpSearch),
    },
    {
      name: "plainText",
      weight: 0.03,
      getFn: (article) => normalizeHelpSearch(article.plainText ?? ""),
    },
  ],
};

const sectionFuseOptions: IFuseOptions<SearchableHelpSection> = {
  includeScore: true,
  ignoreLocation: true,
  minMatchCharLength: 2,
  threshold: 0.36,
  keys: [
    {
      name: "title",
      weight: 0.28,
      getFn: (section) => normalizeHelpSearch(section.title),
    },
    {
      name: "heading",
      weight: 0.24,
      getFn: (section) => normalizeHelpSearch(section.heading),
    },
    {
      name: "keywords",
      weight: 0.18,
      getFn: (section) => section.keywords.map(normalizeHelpSearch),
    },
    {
      name: "questions",
      weight: 0.14,
      getFn: (section) => normalizeHelpQuestions(section.questions),
    },
    {
      name: "description",
      weight: 0.08,
      getFn: (section) => normalizeHelpSearch(section.description),
    },
    {
      name: "snippet",
      weight: 0.05,
      getFn: (section) => normalizeHelpSearch(section.snippet),
    },
    {
      name: "content",
      weight: 0.03,
      getFn: (section) => normalizeHelpSearch(section.content),
    },
  ],
};

export function isMeaningfulHelpQuery(query?: string) {
  const trimmed = query?.trim();
  if (!trimmed) return false;
  return buildSearchTerms(trimmed).length > 0;
}

export function searchClientArticles(
  articles: SearchableArticle[],
  query?: string,
  audience?: HelpAudience,
  category?: string,
): SearchableArticle[] {
  const filtered = articles.filter((article) => {
    if (audience && article.audience !== audience) return false;
    if (category && article.category !== category) return false;
    return true;
  });

  const trimmed = query?.trim();
  if (!trimmed || !isMeaningfulHelpQuery(trimmed)) return filtered;

  return searchWithTerms(filtered, articleFuseOptions, trimmed).map(
    (result) => result.item,
  );
}

export function searchClientSectionResults(
  sections: SearchableHelpSection[],
  query?: string,
  audience?: HelpAudience,
  category?: string,
): Array<{ section: SearchableHelpSection; score: number }> {
  const filtered = sections.filter((section) => {
    if (audience && section.audience !== audience) return false;
    if (category && section.category !== category) return false;
    return true;
  });

  const trimmed = query?.trim();
  if (!trimmed || !isMeaningfulHelpQuery(trimmed)) return [];

  const terms = buildSearchTerms(trimmed);
  const stem = (word: string) =>
    word.length < 5
      ? word
      : word.replace(
          /(iranje|iranju|acija|acije|aciju|ovima|evima|anje|anju|ujem|ujes|ama|ima|ovi|om|em|im|es|a|e|i|u)$/,
          "",
        );
  const words = (text: string) => buildSearchTerms(text).map(stem);
  const matches = (term: string, word: string) => stem(term) === word;
  const documents = filtered.map((section) => ({
    section,
    heading: words(section.heading),
    content: words(section.content),
    metadata: words(
      `${section.title} ${section.description} ${section.keywords.join(" ")} ${normalizeHelpQuestions(section.questions).join(" ")}`,
    ),
  }));
  const specificity = terms.map((term) =>
    Math.log(
      1 +
        filtered.length /
          (1 +
            documents.filter((doc) =>
              [...doc.heading, ...doc.content, ...doc.metadata].some((word) =>
                matches(term, word),
              ),
            ).length),
    ),
  );
  const lexical = new Map(
    documents.map((doc) => {
      const score = terms.reduce(
        (sum, term, index) =>
          sum +
          specificity[index]! *
            (doc.heading.some((word) => matches(term, word))
              ? 3
              : doc.content.some((word) => matches(term, word))
                ? 2
                : doc.metadata.some((word) => matches(term, word))
                  ? 0.5
                  : 0),
        0,
      );
      return [doc.section, score] as const;
    }),
  );
  const candidates = new Map(
    searchWithTerms(filtered, sectionFuseOptions, trimmed).map((result) => [
      result.item,
      result.score,
    ]),
  );
  // Rare words in the actual section can retrieve an answer even when the
  // entire sentence fails fuzzy matching. Shared article metadata weighs less.
  for (const doc of documents) {
    const matchedTerms = terms.filter((term) =>
      [...doc.heading, ...doc.content, ...doc.metadata].some((word) =>
        matches(term, word),
      ),
    );
    if (
      matchedTerms.length >= Math.min(2, terms.length) &&
      terms.some(
        (term, index) =>
          specificity[index]! > 1 &&
          [...doc.heading, ...doc.content].some((word) => matches(term, word)),
      )
    ) {
      if (!candidates.has(doc.section)) candidates.set(doc.section, 1);
    }
  }
  return [...candidates]
    .map(([item, score]) => ({ item, score }))
    .map((result) => ({
      section: result.item,
      // Article-wide keywords appear on every section. Favor the section
      // containing the answer, not whichever shared metadata appears first.
      score:
        result.score -
        (lexical.get(result.item) ?? 0) +
        0.15 *
          (1 -
            terms.filter((term) =>
              normalizeHelpSearch(
                `${result.item.heading} ${result.item.content}`,
              ).includes(term),
            ).length /
              terms.length),
    }))
    .sort((a, b) => a.score - b.score);
}

export function searchClientSections(
  sections: SearchableHelpSection[],
  query?: string,
  audience?: HelpAudience,
  category?: string,
): SearchableHelpSection[] {
  return searchClientSectionResults(sections, query, audience, category).map(
    (result) => result.section,
  );
}

export function getClientCategories(
  articles: SearchableArticle[],
  audience?: HelpAudience,
): string[] {
  return Array.from(
    new Set(
      articles
        .filter((a) => !audience || a.audience === audience)
        .map((a) => a.category),
    ),
  ).sort((a, b) => a.localeCompare(b, "sr"));
}
